import React, { useState } from 'react';
import { Row, Col, Card, Form } from 'react-bootstrap';
import ConfirmDialog from './ConfirmDialog';
import SaveBar from './company/SaveBar';
import { describeBillingChanges } from '../utils/billingNotes';

/**
 * Presentational billing controls form, shared by the full company admin page
 * (CompanyDetail billing tab) and the focused CRM company page (CRMCompanyDetail).
 *
 * Props:
 *  - values: { accountType, paymentFrequency, renewalDate, preferredPointOfContact, freeUsers, paidUsers, overduePayment, usesStripe }
 *  - onFieldChange: (field, value) => void
 *  - onSave: () => void
 *  - isSaving, canEdit
 *  - savedValues (optional): the company as last saved. When given, Save stays disabled until
 *    something changed, and clicking it first shows the change summary in a confirm dialog.
 *  - saveStatus (optional): { type: 'success'|'error'|'warning', message }, shown beside Save.
 *  - saveSuccess (optional): older form of saveStatus, shown as "Saved". onDismissSuccess is no
 *    longer used, since the result sits beside the button instead of in a dismissible alert.
 */
const BillingManagement = ({
  values,
  onFieldChange,
  onSave,
  isSaving,
  saveSuccess,
  canEdit,
  savedValues = null,
  saveStatus = null,
}) => {
  const [confirming, setConfirming] = useState(false);
  const changes = savedValues ? describeBillingChanges(savedValues, values) : null;
  // Without savedValues the form cannot tell what changed, so Save is always available
  const dirty = changes ? changes.length > 0 : null;
  const status = saveStatus || (saveSuccess ? { type: 'success', message: 'Saved' } : null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canEdit || isSaving || dirty === false) return;
    if (changes) {
      setConfirming(true);
    } else {
      onSave();
    }
  };

  const confirmSave = () => {
    setConfirming(false);
    onSave();
  };

  const seatValue = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-light">
        <h5 className="mb-0">Billing</h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <fieldset disabled={!canEdit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="billingAccountType">
                  <Form.Label>Plan</Form.Label>
                  <Form.Select
                    value={values.accountType}
                    onChange={(e) => onFieldChange('accountType', e.target.value)}
                  >
                    <option value="">Select a plan</option>
                    <option value="STANDARD">Standard</option>
                    <option value="PROFESSIONAL">Professional</option>
                    <option value="PREMIUM">Premium</option>
                    <option value="ENTERPRISE">Enterprise</option>
                    <option value="FREE_TRIAL">Free trial</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="billingRenewalDate">
                  <Form.Label>Renewal date</Form.Label>
                  <Form.Control
                    type="date"
                    value={values.renewalDate}
                    onChange={(e) => onFieldChange('renewalDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="billingFrequency">
                  <Form.Label>Billing frequency</Form.Label>
                  <Form.Select
                    value={values.paymentFrequency}
                    onChange={(e) => onFieldChange('paymentFrequency', e.target.value)}
                  >
                    <option value="">Select billing frequency</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUARTERLY">Quarterly</option>
                    <option value="ANNUAL">Annual</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="billingPreferredPointOfContact">
                  <Form.Label>Preferred point of contact</Form.Label>
                  <Form.Control
                    type="text"
                    value={values.preferredPointOfContact}
                    onChange={(e) => onFieldChange('preferredPointOfContact', e.target.value)}
                    placeholder="e.g. name or email address"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="billingFreeUsers">
                  <Form.Label>Free seats</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    value={values.freeUsers}
                    onChange={(e) => onFieldChange('freeUsers', seatValue(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    placeholder="Number of free seats"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="billingPaidUsers">
                  <Form.Label>Paid seats</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    value={values.paidUsers}
                    onChange={(e) => onFieldChange('paidUsers', seatValue(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    placeholder="Number of paid seats"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="billingOverduePayment"
                    label="Overdue payment"
                    checked={values.overduePayment}
                    onChange={(e) => onFieldChange('overduePayment', e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="billingUsesStripe"
                    label="Using Stripe"
                    checked={values.usesStripe}
                    onChange={(e) => onFieldChange('usesStripe', e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </fieldset>

          {canEdit && (
            <SaveBar dirty={dirty} saving={isSaving} status={status} label="Save changes" sticky />
          )}
        </Form>

        {changes && (
          <ConfirmDialog
            show={confirming}
            title="Save billing changes?"
            confirmLabel="Save changes"
            onConfirm={confirmSave}
            onCancel={() => setConfirming(false)}
          >
            <p>These changes take effect straight away and are added to the comments as a note.</p>
            <ul className="mb-0">
              {changes.map((change) => (
                <li key={change.key}>
                  <span className="fw-semibold">{change.label}:</span> {change.from} to {change.to}
                </li>
              ))}
            </ul>
          </ConfirmDialog>
        )}
      </Card.Body>
    </Card>
  );
};

export default BillingManagement;
